import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { SyncStatusBadge, type SyncStatus } from '../components/SyncStatusBadge.js';

interface SyncRun {
  id: string;
  exportId: string;
  exportName: string;
  dataSource: string;
  status: SyncStatus;
  rowsSynced: number | null;
  durationMs: number | null;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

interface ExportOption {
  id: string;
  name: string;
}

interface HistoryStats {
  total_today: number;
  success_rate: number;
  total_rows_today: number;
}

export function History() {
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [exports_, setExports] = useState<ExportOption[]>([]);
  const [stats, setStats] = useState<HistoryStats>({ total_today: 0, success_rate: 0, total_rows_today: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterExport, setFilterExport] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Detail row
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function loadHistory() {
    try {
      setError('');
      const [runsRes, exportsRes] = await Promise.all([
        api.get<{ history: SyncRun[]; total: number }>('/sync/history'),
        api.get<{ exports: ExportOption[] }>('/exports'),
      ]);
      const runsData = runsRes.history ?? [];
      const exportsData = exportsRes.exports ?? [];
      setRuns(runsData);
      setExports(exportsData);

      // Calculate stats from runs
      const today = new Date().toISOString().split('T')[0];
      const todayRuns = runsData.filter((r) => r.startedAt.startsWith(today));
      const completedToday = todayRuns.filter((r) => r.status === 'completed');
      setStats({
        total_today: todayRuns.length,
        success_rate: todayRuns.length > 0 ? Math.round((completedToday.length / todayRuns.length) * 100) : 0,
        total_rows_today: todayRuns.reduce((sum, r) => sum + (r.rowsSynced ?? 0), 0),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadHistory();
  }, []);

  async function handleAction(id: string, action: 'stop' | 'retry') {
    try {
      await api.post(`/sync/history/${id}/${action}`);
      loadHistory(); // Refresh
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action}`);
    }
  }

  function formatDuration(ms: number | null): string {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  }

  // Apply filters
  const filtered = runs.filter((r) => {
    if (filterExport && r.exportId !== filterExport) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterDateFrom && r.startedAt < filterDateFrom) return false;
    if (filterDateTo && r.startedAt > filterDateTo + 'T23:59:59') return false;
    return true;
  });

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">Sync History</h2>
        <p className="text-sm text-gray-500">View recent sync runs and their results.</p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Syncs Today" value={String(stats.total_today)} />
        <StatCard label="Success Rate" value={`${stats.success_rate}%`} ok={stats.success_rate >= 80} />
        <StatCard label="Rows Synced Today" value={stats.total_rows_today.toLocaleString()} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500">Export</label>
          <select
            value={filterExport}
            onChange={(e) => setFilterExport(e.target.value)}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All exports</option>
            {exports_.map((ex) => (
              <option key={ex.id} value={ex.id}>
                {ex.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">From</label>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">To</label>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        {(filterExport || filterStatus || filterDateFrom || filterDateTo) && (
          <button
            onClick={() => {
              setFilterExport('');
              setFilterStatus('');
              setFilterDateFrom('');
              setFilterDateTo('');
            }}
            className="text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* History table */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm text-gray-500">No sync history found.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Export</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Rows</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Started</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((run) => (
                <RunRow
                  key={run.id}
                  run={run}
                  expanded={expandedId === run.id}
                  onToggle={() => setExpandedId(expandedId === run.id ? null : run.id)}
                  formatDuration={formatDuration}
                  onAction={handleAction}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunRow({
  run,
  expanded,
  onToggle,
  formatDuration,
  onAction,
}: {
  run: SyncRun;
  expanded: boolean;
  onToggle: () => void;
  formatDuration: (ms: number | null) => string;
  onAction: (id: string, action: 'stop' | 'retry') => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <td className="px-4 py-3 text-sm font-medium text-gray-900">{run.exportName}</td>
        <td className="px-4 py-3 text-sm text-gray-500">{run.dataSource}</td>
        <td className="px-4 py-3">
          <SyncStatusBadge status={run.status} />
        </td>
        <td className="px-4 py-3 text-right text-sm text-gray-500">
          {run.rowsSynced != null ? run.rowsSynced.toLocaleString() : '-'}
        </td>
        <td className="px-4 py-3 text-right text-sm text-gray-500">
          {formatDuration(run.durationMs)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {new Date(run.startedAt).toLocaleString()}
        </td>
        <td className="px-4 py-3 text-right">
          {(run.status === 'running' || run.status === 'pending') && (
            <button
              onClick={(e) => { e.stopPropagation(); onAction(run.id, 'stop'); }}
              className="rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
            >
              Stop
            </button>
          )}
          {run.status === 'failed' && (
            <button
              onClick={(e) => { e.stopPropagation(); onAction(run.id, 'retry'); }}
              className="rounded bg-brand-50 px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-100"
            >
              Retry
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-gray-50 px-4 py-3">
            <div className="space-y-1">
              <p className="text-xs text-gray-500">
                <span className="font-medium">Run ID:</span> {run.id}
              </p>
              {run.completedAt && (
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Completed:</span> {new Date(run.completedAt).toLocaleString()}
                </p>
              )}
              {run.errorMessage && (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-xs font-medium text-red-800">Error Details</p>
                  <p className="mt-1 text-xs text-red-600 font-mono whitespace-pre-wrap">{run.errorMessage}</p>
                </div>
              )}
              {!run.errorMessage && run.status === 'completed' && (
                <p className="text-xs text-green-600">Sync completed successfully.</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatCard({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${ok === false ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-5 w-32 rounded bg-gray-200" />
        <div className="mt-2 h-4 w-56 rounded bg-gray-200" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}
